import React, { useState, useCallback } from "react";
import { Bluelagoon } from "@bluelagoon";
import axios from "axios";
import PublicFileUpload from "./PublicFileUpload";

export default function ExcelReader({
    config,
    onSubmit,
    searchSheetNameBy,
    headerRow = 1,
    integrationId,
}) {
    const [sheetNames, setSheetNames] = useState([]);
    const [selectedSheet, setSelectedSheet] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [allSheetsData, setAllSheetsData] = useState(null);

    const [file, setFile] = useState(null);

    const handleFetchExcelData = useCallback(
        async (fileUrl, sheetName) => {
            console.log("fileUrl", fileUrl);
            if (!fileUrl) return;

            try {
                setLoading(true);
                setError(null);

                const response = await axios({
                    method: "POST",
                    url: `${Bluelagoon.apiUrl}/proxy/documents/extract-excel`,
                    headers: {
                        "x-api-key": config?.apiKey,
                    },
                    data: {
                        fileUrl: fileUrl,
                        headerRow: parseInt(headerRow) ?? 1,
                        sheet: sheetName,
                        preview: true,
                    },
                });

                const { sheets, allSheetsData } = response.data;

                setAllSheetsData(allSheetsData);

                // Filter sheets if searchSheetNameBy is provided
                const filteredSheets = searchSheetNameBy
                    ? sheets.filter((name) =>
                          name
                              .toLowerCase()
                              .includes(searchSheetNameBy.toLowerCase())
                      )
                    : sheets;

                setSheetNames(filteredSheets);
                setPreview(preview);

                // Auto-select if only one sheet matches
                if (filteredSheets.length === 1) {
                    setSelectedSheet(filteredSheets[0]);
                    if (onSubmit) {
                        onSubmit({
                            data: allSheetsData[filteredSheets[0]],
                        });
                    }
                }

                return response.data.data;
            } catch (err) {
                setError(
                    "Failed to read Excel file. Please ensure it's a valid Excel document."
                );
                console.error("Error reading Excel:", err);
            } finally {
                setLoading(false);
            }
        },
        [file, headerRow, searchSheetNameBy]
    );

    const fetchExcelData = useCallback(
        async (file) => {
            setError(null);
            setFile(file.url);
            await handleFetchExcelData(file.url, selectedSheet);
        },
        [handleFetchExcelData, selectedSheet]
    );

    const handleSheetSelect = useCallback(
        async (sheetName) => {
            setSelectedSheet(sheetName);

            if (onSubmit) {
                onSubmit({ data: allSheetsData[sheetName] });
            }
        },
        [onSubmit, allSheetsData]
    );

    return (
        <div className="space-y-4">
            <PublicFileUpload
                config={config}
                onChange={fetchExcelData}
                accept=".xlsx,.xls,.csv"
                integrationId={integrationId}
            />

            {loading && (
                <div className="flex items-center justify-center p-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
            )}

            {error && (
                <div className="text-red-500 p-4 rounded bg-red-50">
                    {error}
                </div>
            )}

            {sheetNames.length > 0 && (
                <div className="border rounded-lg p-4 bg-white">
                    <h3 className="font-medium mb-3">Select a sheet:</h3>
                    <div className="flex flex-wrap gap-2 [&>*]:min-w-[150px]">
                        {sheetNames.map((sheetName) => (
                            <button
                                key={sheetName}
                                onClick={() => handleSheetSelect(sheetName)}
                                className={`p-3 text-left rounded border transition-colors
                                    ${
                                        selectedSheet === sheetName
                                            ? "border-blue-500 bg-blue-50 text-blue-700"
                                            : "border-gray-200 hover:border-blue-200 hover:bg-blue-50"
                                    }`}
                            >
                                {sheetName}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {selectedSheet && preview?.[selectedSheet] && (
                <div className="border rounded-lg p-4 bg-white overflow-x-auto">
                    <h3 className="font-medium mb-3">
                        Preview of "{selectedSheet}":
                    </h3>
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {preview[selectedSheet][0]?.map(
                                    (header, idx) => (
                                        <th
                                            key={idx}
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {header}
                                        </th>
                                    )
                                )}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {preview[selectedSheet]
                                .slice(1, 6)
                                .map((row, rowIdx) => (
                                    <tr key={rowIdx}>
                                        {row.map((cell, cellIdx) => (
                                            <td
                                                key={cellIdx}
                                                className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                                            >
                                                {cell}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
